import { FC, memo, useCallback, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";

import { useNarrativesContext } from "@components/layout/main-layout/private-layout";
import RationalForm from "@components/pages/project/single-functions/climate-rationale-advisor/form";
import CommonFooterButton from "@commonComponents/footer-button";
import { useStore } from "@stores/root-store";
import { constRoute } from "@utils/route";
import { useForm } from "antd/es/form/Form";
import { useNavigate, useParams } from "react-router-dom";
import { io } from "socket.io-client";
import { DataType } from "../../../../../../types/data-type";
import { baseSocketUrl } from "@api/const";
import { notification } from "@utils/notifications";

const formQuestions = [
  {
    q: <span>Please Fill In the Blanks Below:</span>,
    result: <span>Find below Co-pilot's Output:</span>,
    limit: 1000,
    placeholder: "",
  },
  // {
  //   id: "q3",
  //   q: (
  //     <>3. If The Project Is Cross-Cutting, Enter Its Climate Context Below: </>
  //   ),
  //   limit: 1000,
  //   result: <>Find below Co-pilot's Output:</>,
  //   placeholder:
  //     "Guidance​ Below:\n\n" +
  //     "The baseline scenario description should provide a detailed and concise overview of the current conditions in the project areas, including\n\u00A0\u00A0\u00A0 supporting data and statistics. This should include:\n\n" +
  //     "Current Conditions: Describe the current state of the project areas, including climate-related hazards, infrastructure, and socio-economic conditions.\n\n" +
  //     "Criteria for Selecting Project Areas: Outline the criteria used to select the project areas, including stakeholder engagement processes.\n\n" +
  //     "Quantification of Climate Hazards: Quantify how climate hazards will affect the project's geographical area in terms of loss of lives, value\n\u00A0\u00A0\u00A0 of physical assets, livelihoods, and environmental or social/cultural assets. Demonstrate that climate change is the primary driver of\n\u00A0\u00A0\u00A0 adverse impacts and link climate change to these impacts using literature and data.\n\n" +
  //     "Projected Climate Change Impacts: Outline expected climate impacts by a specific year (e.g., 2030, 2040) in the subject intervention\n\u00A0\u00A0\u00A0 area/country/region, citing relevant sources from the database.\n\n" +
  //     "Climate Vulnerability Reduction: Explicitly state the project's intent to reduce climate vulnerability and improve resilience, highlighting the\n\u00A0\u00A0\u00A0 measures to be taken to mitigate climate-related risks and impacts.",
  // },
  {
    id: "q4",
    q: (
      <>
        3. Enter The Proposed Project’s Details Below.
        <span style={{ color: "#de3b3b" }}>*</span>
      </>
    ),
    result: <>Find below Co-pilot's Output:</>,
    limit: 1000,
    placeholder:
      "Paste your Climate Rationale - Response section here.\n\n" +
      "Guidance:\n\n" +
      "For the most helpful evaluation, please ensure your text clearly outlines the following:\n\n" +
      " • The Solution & Its Logic (Theory of Change): Clearly describe your project's main activities and the logical chain of results (Activities\n\u00A0\u00A0\u00A0 → Outputs → Outcomes) that will solve the climate problem you identified.\n\n" +
      " • Justification for Your Approach: Explain why your chosen solution is the most effective. Briefly mention other alternatives you\n\u00A0\u00A0\u00A0 considered and why this one is superior (e.g., more cost-effective, sustainable, or locally appropriate).\n\n" +
      " • Beneficiaries & Feasibility: Define who the direct beneficiaries are and how they were identified. Acknowledge potential barriers to implementation and describe your\n\u00A0\u00A0\u00A0 strategies to overcome them.",
  },
  {
    id: "q6",
    q: (
      <>
        4. Discuss How the Proposed Intervention Aligns With National Plans And
        Climate Strategies. <span style={{ color: "#de3b3b" }}>*</span>
      </>
    ),
    result: <>Find below Co-pilot's Output:</>,
    placeholder:
      "Provide the details for your Country Ownership section here.\n\n" +
      "Guidance:\n\n" +
      "To generate a strong narrative, please provide the following information:\n\n" +
      " • Alignment with National Plans: List the key national/sectoral policies, strategies, NDC, or NAP that your project helps implement.\n\n" +
      " • Institutional Leadership: Name the lead government ministry or agency. Crucially, describe their active role in developing and\n\u00A0\u00A0\u00A0 designing this project concept.\n\n" +
      " • Stakeholder Consultation: Describe who you consulted (e.g., CSOs, communities, private sector). Provide one specific example of\n\u00A0\u00A0\u00A0 how their feedback changed or improved the final project design.\n\n" +
      " • Government Commitment: Detail any financial (co-financing) or in-kind (staff time, facilities) contributions the government has\n\u00A0\u00A0\u00A0 committed to the project.",
    limit: 1000,
  },
  {
    id: "q7",
    q: <>5. Enter Project Monitoring and Evaluation Structure Below:</>,
    result: <>Find below Co-pilot's Output:</>,
    placeholder:
      "Paste your Monitoring & Evaluation section here.\n\n" +
      "Guidance:\n\n" +
      "For the most helpful evaluation, please ensure your text clearly outlines the following:\n\n" +
      " • Indicators, Baselines & Targets: A clear list of GCF core and project-specific indicators, each with a quantified baseline and a final\n\u00A0\u00A0\u00A0 target. Mention if data will be disaggregated by gender.\n\n" +
      " • M&E System & Responsibilities: A brief description of the data collection methods, who is responsible for M&E activities, and the\n\u00A0\u00A0\u00A0 schedule for key reports and evaluations.\n\n" +
      " • Budget & Capacity: An indicative budget for M&E and a statement on the team's capacity to implement the plan.\n\n" +
      " • Adaptive Management: A sentence explaining how M&E findings will be used to guide and improve the project during its\n\u00A0\u00A0\u00A0 implementation.",
    limit: 1000,
  },
];

export const sectionsMap = [
  {
    text: "Identification",
    section: "i_24",
  },
  // {
  //   text: "1.1 Identification (Cross-cutting)",
  //   section: "pe_48",
  // },
  {
    text: "Response",
    section: "r_64",
  },
  // {
  //   text: "2.1 Potential Barriers",
  //   section: 'pb_76'
  // },
  {
    text: "National Alignment",
    section: "a_84",
  },
  {
    text: "Monitoring and Evaluation",
    section: "me_100",
  },
];

const sections = sectionsMap.map((section) => section.section);

const sectionConfig = {
  i_24: {
    upload: false, // Changed from true to false to hide upload button
  },
  r_64: {
    upload: false, // Changed from true to false to hide upload button
  },
};

const RationalAdvisorPage: FC = () => {
  const nav = useNavigate();
  const socketRef = useRef(null);
  const { type, step } = useParams();
  const { rationalAdvisor, setRationalAdvisor } = useNarrativesContext();
  const [form] = useForm();
  const [firstStepForm] = useForm();
  const {
    user: { getSingleProjectData, projectNameData },
    modelType: { getSelectedModelType },
  } = useStore(null);

  const projectName =
    (projectNameData && JSON.parse(projectNameData).project_name) ||
    localStorage.getItem("projectName");

  const [initialInput, setInitialInput] = useState(null);
  const [isLoading, setIsLoading] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [sectionOutputPresent, setSectionOutputPresent] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [base64Images, setBase64Images] = useState<string[]>([]);

  const [resultsData, setResultsData] = useState("");
  const [isAnswer, setIsAnswer] = useState(false);
  const [firstStepInitialAnswers, setFirstStepInitialAnswers] = useState({
    q1a: "",
    q1b: "",
    q2: "",
    q3: "",
  });

  const results = isSubmitting
    ? ""
    : rationalAdvisor.find((el) => el.section === sections[+step - 1])?.res;

  const section = sections[+step - 1];
  const email = localStorage.getItem("email");
  const isFormPage = type === "form";
  const handleSubmit = useCallback(
    async (saveOnly = false) => {
      if (isFormPage) {
        const isFirstStep = +step === 1;

        const values = isFirstStep
          ? firstStepForm.getFieldsValue()
          : form.getFieldsValue();

        setResultsData("");

        if (saveOnly === true) {
          setIsSaving(true);
        } else {
          setIsSubmitting(true);
        }

        const data: DataType = isFirstStep
          ? {
              section: sections[+step - 1],
              questions: values,
              project_name: projectName,
              email: email,
              model: getSelectedModelType ?? "Titan",
            }
          : {
              section: sections[+step - 1],
              questions: values,
              project_name: projectName,
              email: email,
              model: getSelectedModelType ?? "Titan",
            };

        if (saveOnly === true) {
          data.save_only = true;
        }

        console.log("payload body ------------->", data);

        if (base64Images) {
          data.questions.images = base64Images;
        }

        const socket = io(baseSocketUrl, {
          transports: ["websocket", "polling"],
        });

        socketRef.current = socket;
        socketRef?.current?.emit("climateRationale", data);

        let timeoutId;
        if (saveOnly === true) {
          timeoutId = setTimeout(() => {
            socket.disconnect();
            setIsSaving(false);
            notification.error("An error occurred: Save request timed out.");
          }, 10000); // 10 seconds timeout
        }

        socketRef?.current?.on("answer", (answer) => {
          const isStopGeneration = localStorage.getItem("isStopGeneration");

          if (
            saveOnly === true &&
            answer === "User input is saved successfully"
          ) {
            clearTimeout(timeoutId); // Cancel timeout if response received
            socket.disconnect();
            setIsSaving(false);
            localStorage.removeItem("isStopGeneration");
            notification.success("Project saved successfully!");
            return nav(constRoute?.existingProject);
          }

          if (!isSubmitting && answer) {
            setIsSubmitting(true);
          }

          if (answer) {
            setIsAnswer(true);
          }

          if (isStopGeneration || answer === "#####") {
            socket.disconnect();
            setIsSubmitting(false);
            setIsAnswer(false);
            localStorage.removeItem("isStopGeneration");
            return;
          }

          form.setFieldValue(
            `q${+step + 1}`,
            (form?.getFieldValue(`q${+step + 1}`) ?? "") + answer,
          );
        });

        socket.on("disconnect", () => {
          clearTimeout(timeoutId); // Prevent timeout from triggering after disconnect
          localStorage.removeItem("isStopGeneration");
          setIsSubmitting(false);
          setIsAnswer(false);
        });

        if (saveOnly !== true) {
          nav(constRoute?.rationalAdvisorProject + "/results/" + step);
        }
      } else {
        if (saveOnly === true) {
          setIsSaving(true);
          await new Promise((resolve) => setTimeout(resolve, 3000));
          setIsSaving(false);
          notification.success("Project saved successfully!");
          return nav(constRoute?.existingProject);
        }

        if (formQuestions.length < +step + 1) {
          nav(constRoute?.gcfCongratulation + "/rational-advisor");
          return;
        }
        setIsSubmitting(false);
        setIsAnswer(false);
        nav(constRoute?.rationalAdvisorProject + "/form/" + (+step + 1));
      }
    },
    [type, step, getSelectedModelType, base64Images],
  );

  const handleGoBack = () => {
    socketRef?.current?.disconnect();
    setIsSubmitting(false);
    setIsAnswer(false);
    // if (isSubmitting) return;
    if (+step === 1 && type == "results") {
      nav(constRoute?.rationalAdvisorProject + "/form/1");

      return;
    }

    if (+step < 2) {
      nav(constRoute.projectName + "/rational-advisor");
      return;
    }

    if (type === "results") {
      nav(constRoute?.rationalAdvisorProject + "/form/" + +step);
    } else {
      nav(constRoute?.rationalAdvisorProject + "/results/" + (+step - 1));
    }
  };

  const handleSaveQuit = () => {
    handleSubmit(true);
  };

  const handleViewReport = () => {
    nav(constRoute?.rationalAdvisorProject + "/results/" + +step);
  };

  const getProjectData = async () => {
    const data = {
      section: sections[+step - 1].toLowerCase(),
      project_name: projectName,
      functionality: "climate rationale",
    };

    setIsLoading(true);
    setSectionOutputPresent(false);
    setSelectedFiles(null);

    localStorage.setItem("data-for-tokens", JSON.stringify(data));

    await getSingleProjectData(data)
      .then((state) => {
        if (+step === 1) {
          setFirstStepInitialAnswers(state?.questions);
        } else {
          setInitialInput(state?.questions);
        }

        // TODO: a bug will bring you here
        if (state?.questions?.images) {
          setBase64Images(state?.questions?.images);
        }

        if (+step === 1) {
          if (!isSubmitting) {
            form.setFieldValue(
              [`q${+step + 1}`],
              state?.generated_narratives?.find(Boolean),
            );
          }
        } else {
          if (isFormPage) {
            form.setFieldValue(
              [formQuestions[+step - 1].id],
              state?.questions[formQuestions[+step - 1].id],
            );
          } else {
            if (!isSubmitting) {
              form.setFieldValue(
                [`q${+step + 1}`],
                state?.generated_narratives?.find(Boolean),
              );
            }
          }
        }
        setRationalAdvisor((prev) => {
          return prev.map((el) => {
            if (el.section === sections[+step - 1]) {
              const obj = {
                ...el,
                res:
                  state?.generated_narratives?.map((el) => el).join("") || "",
              };

              if (obj.res !== "") {
                setSectionOutputPresent(true);
              }

              return obj;
            } else {
              return el;
            }
          });
        });
      })
      .finally(() => setIsLoading(false));

    if (+step < 1 || formQuestions.length + 1 <= +step) {
      nav(constRoute?.gcfCongratulation + "/rational-advisor");
    }
  };

  const handleStopGenerate = () => {
    if (socketRef) {
      socketRef.current.disconnect();
    }
    setIsSubmitting(false);
    setIsAnswer(false);
  };

  useEffect(() => {
    setSelectedFiles(null);
    localStorage.removeItem("isStopGeneration");
    getProjectData();
  }, [step, type]);

  useEffect(() => {
    form.resetFields();
    firstStepForm.resetFields();
    setIsValid(true);
  }, [type === "results"]);

  useEffect(() => {
    socketRef?.current?.disconnect();
    form.resetFields();
    setIsSubmitting(false);
    setIsAnswer(false);
    setResultsData(
      rationalAdvisor.find((el) => el.section === sections[+step - 1])?.res ||
        "",
    );
  }, [step]);

  useEffect(() => {
    const preventClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };

    const elements = document.getElementsByClassName("navigation-element-js");

    for (let i = 0; i < elements.length; i++) {
      if (isLoading) {
        elements[i].addEventListener("click", preventClick);
        if (i !== +step - 1) {
          // @ts-ignore
          elements[i].style.cursor = "not-allowed";
        }
      } else {
        elements[i].removeEventListener("click", preventClick);
        // @ts-ignore
        elements[i].style.cursor = "";
      }
    }
    return () => {
      for (let i = 0; i < elements.length; i++) {
        elements[i].removeEventListener("click", preventClick);
      }
    };
  }, [isLoading]);

  useEffect(() => {
    return () => {
      socketRef?.current?.disconnect();
    };
  }, []);

  return (
    <div style={{ padding: "30px 52px 122px" }}>
      <RationalForm
        form={form}
        firstStepForm={firstStepForm}
        value={results}
        isSubmitting={isSubmitting}
        initialState={initialInput}
        handleSubmit={handleSubmit}
        allowUpload={sectionConfig[section] && sectionConfig[section].upload}
        isFormPage={isFormPage}
        Text={
          isFormPage
            ? formQuestions[+step - 1]?.q
            : formQuestions[+step - 1]?.result || results[`result${step}`] || ""
        }
        placeholder={formQuestions[+step - 1]?.placeholder}
        maxLength={formQuestions[+step - 1]?.limit}
        disabled={type === "results" || isLoading}
        section={sections[+step - 1]}
        selectedFiles={selectedFiles}
        setSelectedFiles={setSelectedFiles}
        setIsValid={setIsValid}
        isValid={isValid}
        firstStepInitialAnswers={firstStepInitialAnswers}
        base64Images={base64Images}
        setBase64Images={setBase64Images}
        projectName={projectName}
        handleStopGenerate={handleStopGenerate}
        handleViewReport={handleViewReport}
        sectionOutputPresent={sectionOutputPresent}
        isLoading={isLoading}
        isAnswer={isAnswer}
      />
      <CommonFooterButton
        customStyle={{ left: 0 }}
        isSaving={isSaving}
        isLoadingSubmit={false}
        handleSubmit={handleSubmit}
        handlegoback={handleGoBack}
        isSubmitting={isSubmitting}
        handleSaveAndQuit={handleSaveQuit}
        isSubmit={type === "results"}
        // handleQuickNext={constRoute?.contextAndBaselineResults}
        form={+step === 1 ? firstStepForm : form}
        setIsSubmitting={setIsSubmitting}
        disableSubmit={!isValid || isLoading}
      />
    </div>
  );
};

export default memo(observer(RationalAdvisorPage));
